import { redirectToAuthLogin } from '@offer/auth-lib'
import { createShellBffClient, ShellBffHttpError } from '@offer/bff-client-core'

const shellBffClient = createShellBffClient({
  resolvePath: (child, path) => `/bff/gateway/${child}/${path.replace(/^\/+/, '')}`,
  fetchFn: (...args: Parameters<typeof fetch>) => fetch(...args),
})

const directShellBffClient = createShellBffClient({
  resolvePath: (_child, path) => `/bff/${path.replace(/^\/+/, '')}`,
  fetchFn: (...args: Parameters<typeof fetch>) => fetch(...args),
})

// Single-flight cache for the CSRF token — valid for the lifetime of the session.
// Resets automatically on any fetch failure so the next call will retry.
let csrfTokenPromise: Promise<string> | null = null

function getOrFetchCsrfToken(): Promise<string> {
  csrfTokenPromise ??= directShellBffClient
    .request<{ csrfToken: string }>({ child: 'direct', path: '/auth/csrf', credentials: 'include' })
    .then(data => data.csrfToken)
    .catch((err) => {
      csrfTokenPromise = null
      throw err
    })
  return csrfTokenPromise
}

async function requestWithAuthRedirect<T>(request: Promise<T>): Promise<T> {
  try {
    return await request
  }
  catch (error) {
    if (error instanceof ShellBffHttpError && error.status === 401) {
      redirectToAuthLogin('/bff/auth/login')
    }

    throw error
  }
}

export function invalidateCsrfToken(): void {
  csrfTokenPromise = null
}

export async function callBFF<T>(method: string, path: string, data?: object, options: { signal?: AbortSignal } = {}): Promise<T> {
  return await requestWithAuthRedirect(shellBffClient.request<T>({
    child: 'offer-catalog',
    path,
    method,
    body: data,
    signal: options.signal,
    credentials: 'include',
  }))
}

export async function callDirectBFF<T>(method: string, path: string, data?: object, headers?: Record<string, string>): Promise<T> {
  const upperMethod = method.toUpperCase()
  const isMutation = upperMethod !== 'GET' && upperMethod !== 'HEAD' && upperMethod !== 'OPTIONS'
  const csrfToken = isMutation ? await requestWithAuthRedirect(getOrFetchCsrfToken()) : undefined

  return await requestWithAuthRedirect(directShellBffClient.request<T>({
    // `child` is required by the shared client type, but ignored by the direct resolver.
    child: 'direct',
    path,
    method,
    body: data,
    headers: csrfToken ? { 'x-csrf-token': csrfToken, ...headers } : headers,
    credentials: 'include',
  }))
}
